import { useEffect, useState } from "react";

const API_URL = "https://open.er-api.com/v6/latest/USD";
const QUOTES = ["NGN", "KES", "ZAR"];

const FxTicker = ({ dark = false, compact = false }) => {
  const [data, setData] = useState(undefined);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    async function load() {
      try {
        const res = await fetch(API_URL, { signal: controller.signal, headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json?.result !== "success" || !json?.rates) throw new Error("Unexpected response");
        const rates = QUOTES.map((q) => ({ quote: q, rate: json.rates[q] })).filter((r) => Number.isFinite(r.rate) && r.rate > 0);
        if (rates.length !== QUOTES.length) throw new Error("Missing rates");
        const date = json.time_last_update_unix ? new Date(json.time_last_update_unix * 1000).toISOString().slice(0, 10) : undefined;
        setData({ rates, date });
      } catch {
        setData(null);
      }
    }

    load();
    const id = setInterval(load, 30 * 60 * 1000);
    return () => {
      clearTimeout(timeout);
      clearInterval(id);
      controller.abort();
    };
  }, []);

  if (!data) return null;

  if (compact) {
    return (
      <div data-testid="fx-ticker" className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs" aria-label="Reference currency exchange rates">
        <span className="flex items-center gap-1.5 font-semibold text-[#0F172A]">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Live FX reference
        </span>
        {data.rates.map((r) => (
          <span key={r.quote} className="whitespace-nowrap font-medium text-[#64748B]">
            USD → {r.quote} <span className="font-semibold text-[#1E3A6E]">{r.rate.toLocaleString("en-US", { maximumFractionDigits: 2 })}</span>
          </span>
        ))}
        {data.date && <span className="text-[#94A3B8]">{data.date}</span>}
      </div>
    );
  }

  return (
    <div
      data-testid="fx-ticker"
      className={`mt-6 inline-flex max-w-full flex-wrap items-center gap-x-4 gap-y-1.5 rounded-full border px-4 py-2 text-xs backdrop-blur ${
        dark ? "border-white/10 bg-white/5" : "border-[#E2E8F0] bg-white/80"
      }`}
      aria-label="Reference currency exchange rates"
    >
      <span className={`flex items-center gap-1.5 font-semibold ${dark ? "text-white" : "text-[#0F172A]"}`}>
        <span className="relative flex h-2 w-2">
          <span className="absolute h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        Live FX reference
      </span>
      {data.rates.map((r) => (
        <span key={r.quote} className={`whitespace-nowrap font-medium ${dark ? "text-slate-400" : "text-[#64748B]"}`}>
          USD → {r.quote}{" "}
          <span className={`font-semibold ${dark ? "text-[#6BA3E8]" : "text-[#1E3A6E]"}`}>
            {r.rate.toLocaleString("en-US", { maximumFractionDigits: 2 })}
          </span>
        </span>
      ))}
      {data.date && <span className={dark ? "text-slate-500" : "text-[#94A3B8]"}>{data.date}</span>}
    </div>
  );
};

export default FxTicker;
